import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, CheckCircle, ArrowRight } from 'lucide-react';
import { apiUrl } from '../../utils/api';

interface CampaignSummary {
  id: string;
  title: string;
  description: string;
  location: { city: string; state: string };
  progressPercentage: number;
  isVerified: boolean;
}

export function ActiveCampaigns() {
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchCampaigns() {
      try {
        const res = await fetch(apiUrl('/api/campaigns/public?limit=4&offset=0'));
        const data = await res.json();
        if (data.success && data.data) {
          setCampaigns(data.data);
        }
      } catch {
        // Silently fail — section just won't show
      } finally {
        setIsLoading(false);
      }
    }
    fetchCampaigns();
  }, []);

  // Don't render section if no campaigns available
  if (!isLoading && campaigns.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-3">Campañas activas</h2>
        <p className="text-gray-600">Causas verificadas que necesitan tu ayuda ahora mismo.</p>
      </div>

      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-5 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
              <div className="h-3 bg-gray-100 rounded w-full mb-2" />
              <div className="h-3 bg-gray-100 rounded w-2/3 mb-4" />
              <div className="h-2 bg-gray-100 rounded-full w-full" />
            </div>
          ))}
        </div>
      ) : (
        <div className={`grid gap-6 ${
          campaigns.length === 1 ? 'max-w-sm mx-auto' :
          campaigns.length === 2 ? 'md:grid-cols-2 max-w-2xl mx-auto' :
          campaigns.length === 3 ? 'md:grid-cols-2 lg:grid-cols-3' :
          'md:grid-cols-2 lg:grid-cols-4'
        }`}>
          {campaigns.map((campaign) => (
            <Link
              key={campaign.id}
              to={`/campaigns/${campaign.id}`}
              className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md hover:border-indigo-200 transition-all group"
            >
              {/* Badge */}
              {campaign.isVerified && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700 mb-3">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Verificada
                </span>
              )}

              {/* Title */}
              <h3 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2 group-hover:text-indigo-600 transition-colors">
                {campaign.title}
              </h3>

              {/* Location */}
              <div className="flex items-center space-x-1 text-xs text-gray-500 mb-3">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{campaign.location.city}, {campaign.location.state}</span>
              </div>

              {/* Progress bar */}
              <div>
                <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                  <span>Progreso</span>
                  <span className="font-medium">{Math.round(campaign.progressPercentage)}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className="h-1.5 rounded-full bg-indigo-500 transition-all"
                    style={{ width: `${Math.min(campaign.progressPercentage, 100)}%` }}
                  />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* CTA */}
      <div className="text-center mt-8">
        <Link
          to="/marketplace"
          className="inline-flex items-center space-x-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
        >
          <span>Ver todas las campañas</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}
